/**
 * Active Directory groups — list, membership, and editing it.
 *
 * WHY THIS EXISTS
 * Reported: "Cannot see or edit Active Directory groups." The console managed
 * domain users and could not show a single group.
 *
 * In a Windows estate group membership IS the access model. "Who can reach the
 * finance share", "who is a Domain Admin", "what does this leaver still have"
 * are all group questions, and a directory tool that cannot answer them is
 * missing the half people bring to it most often.
 *
 * SECURITY GROUPS ARE VISUALLY DISTINCT FROM DISTRIBUTION GROUPS
 * A distribution group grants no access at all. Rendering the two identically
 * is how somebody adds a person to the wrong one and then wonders why their
 * permissions did not change — or worse, believes access was granted when it
 * was not.
 *
 * WRITES ARE QUEUED, NOT INSTANT
 * Every change goes to the AD runner as a command, the same path as every other
 * directory change, so it is audited and survives the runner being briefly
 * offline. The UI says "queued" rather than pretending the change has landed —
 * claiming success before the DC has confirmed it is how a technician moves on
 * from a job that did not happen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import ActionButton from "./ActionButton";
import { formatDateTime } from "../utils/datetime";

type Group = {
  id: number;
  sam_account_name: string;
  name: string | null;
  description: string | null;
  category: string | null;
  scope: string | null;
  distinguished_name: string | null;
  mail: string | null;
  is_present: boolean;
  member_count: number;
  last_synced_at: string | null;
};

type Member = {
  sam_account_name: string;
  name: string | null;
  member_class: string | null;
};

/** Groups whose membership is worth a second look on any review. Matched on the
 *  well-known names rather than SID, because the sync stores SamAccountName —
 *  a rename would lose the highlight, which is a cosmetic loss, not a security
 *  one (the group still lists its members either way). */
const PRIVILEGED = new Set([
  "domain admins",
  "enterprise admins",
  "schema admins",
  "administrators",
  "account operators",
  "backup operators",
  "server operators",
  "print operators",
  "group policy creator owners",
  "dnsadmins",
]);

type SortKey = "name" | "members" | "type" | "scope" | "synced";
type View = "all" | "security" | "distribution" | "privileged" | "empty";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "members", label: "Members" },
  { key: "type", label: "Type" },
  { key: "scope", label: "Scope" },
  { key: "synced", label: "Last synced" },
];

const VIEWS: { key: View; label: string; hint: string }[] = [
  { key: "all", label: "All", hint: "Every group" },
  { key: "security", label: "Security", hint: "Groups that actually grant access" },
  { key: "distribution", label: "Distribution", hint: "Mail only — grants no access" },
  { key: "privileged", label: "Privileged", hint: "The groups worth reviewing first" },
  { key: "empty", label: "Empty", hint: "No members — often left over" },
];

export default function ADGroups({ customerId }: { customerId: number | null }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [members, setMembers] = useState<Record<number, Member[]>>({});
  const [addDraft, setAddDraft] = useState("");
  // Reported: "no method to sort (no sortable menu present)". The list is a
  // set of expandable cards rather than a table, so column headers are not
  // available to click — an explicit control is.
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [view, setView] = useState<View>("all");

  const load = useCallback(async () => {
    if (!customerId) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/api/v1/ad/groups", { params: { customer_id: customerId } });
      setGroups(r.data || []);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load groups");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const openGroup = async (g: Group) => {
    if (openId === g.id) {
      setOpenId(null);
      return;
    }
    setOpenId(g.id);
    setAddDraft("");
    if (members[g.id]) return;
    try {
      const r = await api.get(`/api/v1/ad/groups/${g.id}/members`);
      setMembers((m) => ({ ...m, [g.id]: r.data || [] }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load that group's members");
    }
  };

  const addMember = async (g: Group) => {
    const member = addDraft.trim();
    if (!member) return;
    setError("");
    try {
      await api.post(`/api/v1/ad/groups/${g.id}/members`, { member });
      setAddDraft("");
      // Optimistically shown as pending. The list refreshes from AD on the next
      // sync — saying "added" now would claim something the DC has not yet
      // confirmed.
      setNotice(
        `Queued: add ${member} to ${g.name || g.sam_account_name}. It will appear after the next directory sync.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not queue that change");
    }
  };

  const removeMember = async (g: Group, m: Member) => {
    if (
      !window.confirm(
        `Remove ${m.name || m.sam_account_name} from ${g.name || g.sam_account_name}?\n\n` +
          `This changes real access in Active Directory.`,
      )
    )
      return;
    setError("");
    try {
      await api.delete(
        `/api/v1/ad/groups/${g.id}/members/${encodeURIComponent(m.sam_account_name)}`,
      );
      setNotice(
        `Queued: remove ${m.sam_account_name} from ${g.name || g.sam_account_name}. It will clear after the next directory sync.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not queue that change");
    }
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = groups.filter((g) => {
      if (q) {
        const hit =
          (g.name || "").toLowerCase().includes(q) ||
          g.sam_account_name.toLowerCase().includes(q) ||
          (g.description || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (view === "security") return g.category === "security";
      if (view === "distribution") return g.category === "distribution";
      if (view === "privileged") return PRIVILEGED.has((g.name || "").toLowerCase());
      if (view === "empty") return g.member_count === 0;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const label = (g: Group) => (g.name || g.sam_account_name).toLowerCase();
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "members":
          // Ties on member count fall back to the name, so the order is stable
          // and a re-render can't shuffle equal rows around under the cursor.
          return (a.member_count - b.member_count) * dir || label(a).localeCompare(label(b));
        case "type":
          return ((a.category || "").localeCompare(b.category || "") * dir ||
            label(a).localeCompare(label(b)));
        case "scope":
          return ((a.scope || "").localeCompare(b.scope || "") * dir ||
            label(a).localeCompare(label(b)));
        case "synced": {
          // Nulls last regardless of direction — "never synced" is not a date
          // and sorting it as one puts it either first or last by accident.
          const at = a.last_synced_at ? Date.parse(a.last_synced_at) : null;
          const bt = b.last_synced_at ? Date.parse(b.last_synced_at) : null;
          if (at === null && bt === null) return label(a).localeCompare(label(b));
          if (at === null) return 1;
          if (bt === null) return -1;
          return (at - bt) * dir;
        }
        default:
          return label(a).localeCompare(label(b)) * dir;
      }
    });
  }, [groups, search, sortKey, sortDir, view]);

  if (!customerId) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Pick a customer to see their Active Directory groups.
      </p>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Groups{" "}
          {groups.length > 0 && (
            <span className="text-gray-400">
              ({shown.length === groups.length ? groups.length : `${shown.length} of ${groups.length}`})
            </span>
          )}
        </h2>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search groups…"
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-64"
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Synced from the domain controller. Changes are queued to the AD runner and appear here
        after the next sync.
      </p>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              title={v.hint}
              onClick={() => setView(v.key)}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                view === v.key
                  ? "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <label
            htmlFor="ad-group-sort"
            className="text-xs text-gray-500 dark:text-gray-400"
          >
            Sort by
          </label>
          <select
            id="ad-group-sort"
            value={sortKey}
            onChange={(e) => {
              const next = e.target.value as SortKey;
              setSortKey(next);
              // Member count and dates are almost always wanted largest/newest
              // first; names are not. Guessing right saves a second click on
              // the case that matters.
              setSortDir(next === "members" || next === "synced" ? "desc" : "asc");
            }}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            title={sortDir === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"}
            aria-label={`Sort ${sortDir === "asc" ? "ascending" : "descending"}`}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-sm rounded-lg px-3 py-2 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-xs shrink-0">
            ✕
          </button>
        </div>
      )}

      {loading && <div className="text-xs text-gray-400 dark:text-gray-500">Loading groups…</div>}
      {!loading && groups.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No groups synced yet. Run a directory sync from the Users tab — groups come across with
          it.
        </p>
      )}

      <ul className="space-y-1.5">
        {shown.map((g) => {
          const privileged = PRIVILEGED.has((g.name || "").toLowerCase());
          const isOpen = openId === g.id;
          return (
            <li
              key={g.id}
              className={`rounded-lg border ${
                privileged
                  ? "border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-500/5"
                  : "border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
              }`}
            >
              <button
                type="button"
                onClick={() => openGroup(g)}
                className="w-full text-left px-3 py-2 flex items-center gap-2 flex-wrap"
              >
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {g.name || g.sam_account_name}
                </span>
                {/* A distribution group grants NO access. Showing it the same as
                    a security group invites someone to add a person to the wrong
                    one and believe access was granted. */}
                {g.category === "distribution" && (
                  <span className="text-[10px] rounded-full bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-300 px-1.5 py-0.5 font-medium">
                    Distribution — grants no access
                  </span>
                )}
                {privileged && (
                  <span className="text-[10px] rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 font-medium">
                    Privileged
                  </span>
                )}
                {!g.is_present && (
                  <span className="text-[10px] rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5">
                    No longer in AD
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                  {g.member_count} {g.member_count === 1 ? "member" : "members"}
                </span>
                <span className="text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700 pt-2">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 flex flex-wrap gap-x-3">
                    <span>{g.sam_account_name}</span>
                    {g.scope && <span>· {g.scope}</span>}
                    {g.category && <span>· {g.category}</span>}
                    {g.last_synced_at && <span>· synced {formatDateTime(g.last_synced_at)}</span>}
                  </div>
                  {g.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">{g.description}</p>
                  )}

                  {(members[g.id] || []).length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      No members, or membership could not be read from the domain controller.
                    </p>
                  ) : (
                    <ul className="space-y-1 mb-2">
                      {(members[g.id] || []).map((m) => (
                        <li
                          key={m.sam_account_name}
                          className="group flex items-center gap-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
                        >
                          {/* A nested group is not a person. Rendering them the
                              same is how somebody removes a whole department
                              thinking they removed one account. */}
                          <span className="text-gray-400 w-4 shrink-0">
                            {m.member_class === "group" ? "▣" : m.member_class === "computer" ? "🖥" : "👤"}
                          </span>
                          <span className="text-gray-800 dark:text-gray-200 truncate">
                            {m.name || m.sam_account_name}
                          </span>
                          <span className="text-gray-400 truncate">{m.sam_account_name}</span>
                          {m.member_class === "group" && (
                            <span className="text-[10px] rounded bg-violet-100 dark:bg-violet-500/20 text-violet-800 dark:text-violet-300 px-1 shrink-0">
                              nested group
                            </span>
                          )}
                          {/* Always visible, not hover-revealed. Reported: "I
                              do not see a method to remove someone" — it was
                              here, at opacity-0 until the row was hovered,
                              which on a touch device is no affordance at all
                              and on a mouse is one you have to find by
                              accident. It still darkens on hover; it just no
                              longer hides. */}
                          <button
                            type="button"
                            onClick={() => removeMember(g, m)}
                            className="ml-auto shrink-0 rounded border border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[11px] text-gray-500 dark:text-gray-400 hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:hover:border-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                            aria-label={`Remove ${m.name || m.sam_account_name} from ${g.name || g.sam_account_name}`}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      value={addDraft}
                      onChange={(e) => setAddDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addMember(g);
                      }}
                      placeholder="SamAccountName to add…"
                      className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-xs"
                    />
                    <ActionButton
                      onAction={() => addMember(g)}
                      variant="raw"
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                      pendingLabel="Queuing…"
                    >
                      Add member
                    </ActionButton>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
